// Swiper-backed carousels for [data-wfc-carousel] (Venues, Destinations, etc.).
// Import it here instead of relying on the legacy application pack: redesigned
// pages use the isolated wfc_page pack and otherwise render an uninitialised,
// full-width first slide.
// Tracks instances so they can be destroyed before a Turbolinks cache snapshot.

import Swiper from "../../swiper-bundle.min";

const instances = new WeakMap();
let live = [];

export function initCarousels(root = document) {
  const SwiperConstructor = window.Swiper || Swiper;
  if (!SwiperConstructor) return;
  const nodes = root.querySelectorAll("[data-wfc-carousel]:not([data-carousel-bound])");

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  nodes.forEach((node) => {
    node.setAttribute("data-carousel-bound", "");
    const pvAttr  = node.getAttribute("data-per-view") || "3";
    const auto    = pvAttr === "auto";          // fixed-width slides (e.g. 240px city cards)
    const single  = pvAttr === "1";             // one centred slide (e.g. testimonials)
    const perView = auto ? "auto" : parseFloat(pvAttr);
    const gap     = parseInt(node.getAttribute("data-gap") || "24", 10);
    const wrap    = node.closest("section, .wfc") || node.parentElement;

    const fade    = node.getAttribute("data-effect") === "fade";
    const loop    = node.hasAttribute("data-loop");
    const autoMs  = parseInt(node.getAttribute("data-autoplay") || "0", 10);
    const pagEl   = wrap?.querySelector("[data-carousel-pagination]") || null;

    const config = {
      slidesPerView: auto ? "auto" : (single ? 1 : 1.15),
      spaceBetween: gap,
      grabCursor: true,
      speed: fade ? 700 : 520,
      loop,
      a11y: { enabled: true },
      keyboard: { enabled: true },
      navigation: {
        prevEl: wrap?.querySelector("[data-carousel-prev]") || null,
        nextEl: wrap?.querySelector("[data-carousel-next]") || null,
      },
      // Single-slide carousels stay 1-up at every width; multi-up rails step up.
      breakpoints: (auto || single) ? undefined : {
        576:  { slidesPerView: 2.1 },
        992:  { slidesPerView: Math.min(perView, 3) },
        1200: { slidesPerView: perView },
      },
    };

    if (fade) {
      config.effect = "fade";
      config.fadeEffect = { crossFade: true };
    }
    if (pagEl) {
      config.pagination = { el: pagEl, clickable: true };
    }
    if (autoMs > 0 && !reduceMotion) {
      config.autoplay = { delay: autoMs, disableOnInteraction: false, pauseOnMouseEnter: true };
    }

    const swiper = new SwiperConstructor(node, config);

    // Reflect scroll position onto the rail wrapper so CSS edge-fades can lift
    // at the extremes (see .n-city-stories .n-rail). Harmless on rails with no
    // fade styling — it only toggles classes.
    const rail = node.closest(".n-rail");
    if (rail) {
      const syncEdges = () => {
        rail.classList.toggle("is-at-start", swiper.isBeginning);
        rail.classList.toggle("is-at-end", swiper.isEnd);
      };
      swiper.on("progress", syncEdges);
      swiper.on("slideChange", syncEdges);
      swiper.on("resize", syncEdges);
      syncEdges();
    }

    instances.set(node, swiper);
    live.push(swiper);
  });
}

export function destroyCarousels() {
  live.forEach((s) => { try { s.destroy(true, true); } catch (e) {} });
  live = [];
  document
    .querySelectorAll("[data-carousel-bound]")
    .forEach((n) => n.removeAttribute("data-carousel-bound"));
}
